import React, { useCallback, useContext, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';

import DialogTitleWithCloseButton from '../../../../core/components/DialogTitleWithCloseButton';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import { CircularProgress, makeStyles } from '@material-ui/core';
import ImportPermissionsContext from './ImportPermissionsContext';
import Button from '@material-ui/core/Button';
import Confirm from '../../../../core/components/forked/layout/Confirm';
import UserApi from '../../../../core/apis/UserApi';
import { useNotify, useRefresh } from 'react-admin';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import pullAllBy from 'lodash/pullAllBy';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import { omit } from 'lodash';

const useStyles = makeStyles({
  stepper: {
    backgroundColor: '#FFFFFF',
    padding: '0 0 0px',
    display: 'inline-flex'
  },
  leftWrapper: {
    flexGrow: 1
  },
  button: {
    '& span': {
      padding: 0
    }
  },
  backButton: {
    marginLeft: '8px',
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  saveButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  root: {
    paddingTop: '10px',
    paddingBottom: '10px'
  },
  paperWidthFalse: {
    height: '100%',
    width: '100%'
  },
  dialogContent: {
    padding: '0px',
    display: 'flex',
    flexDirection: 'column',
    height: '80vh'
  },
  stepperContainer: {
    backgroundColor: '#FFFFFF',
    padding: '11px 8px',
    display: 'flex',
    borderBottom: '1px solid #E0E0E0'
  },
  stepContainer: {
    height: '100%',
    padding: '16px',
    display: 'flex'
  },
  progressIndicator: {
    width: '20px !important',
    height: '20px !important',
    marginRight: '10px',
    color: 'white'
  }
});

const ImportPermissionsGroupsDialog = props => {
  const { open, setOpenImportPermissionGroups } = props;
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loadingForImport, setLoadingForImport] = useState(false);
  const {
    currentStep,
    steps,
    getActiveStep,
    userToCopy,
    increaseStep,
    decreaseStep,
    updateUser,
    oldAuthorizations,
    newAuthorizationsToImport,
    userId,
    authorizationsUserChosen
  } = useContext(ImportPermissionsContext);
  const classes = useStyles();
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const handleClickSave = useCallback(() => {
    if (currentStep.index === 1) {
      decreaseStep();
    }
    updateUser(null);
    setOpenConfirm(false);
    setOpenImportPermissionGroups(false);
  }, [setOpenImportPermissionGroups, decreaseStep, updateUser, currentStep]);

  const handleClose = useCallback(() => {
    if (userToCopy !== null) {
      setOpenConfirm(true);
    } else {
      setOpenImportPermissionGroups(false);
    }
  }, [setOpenImportPermissionGroups, userToCopy]);

  const handleImportAuthorizations = async () => {
    setLoadingForImport(true);
    let error = false;

    let authorizationsToDelete = [];
    let authorizationsUserChosenCopy = cloneDeep(authorizationsUserChosen);
    let authorizationsToCreate = [];

    //map the latest changes array to delete or add new authorizations to the initial array
    newAuthorizationsToImport.forEach(newItem =>
      newItem.checked
        ? authorizationsToCreate.push(omit(newItem, ['checked']))
        : authorizationsToDelete.push(omit(newItem, ['checked']))
    );

    pullAllBy(authorizationsUserChosenCopy, authorizationsToDelete, 'id');
    const finalArrayToImport = map(
      oldAuthorizations.concat(
        authorizationsUserChosenCopy.concat(authorizationsToCreate)
      ),
      item => ({
        ...item,
        userId
      })
    );

    // Request for creation
    if (finalArrayToImport.length > 0) {
      await UserApi.createUserauthorizations(finalArrayToImport).catch(err => {
        error = true;
        notifyServerError(err);
      });
    }
    if (!error) {
      notify('ra.notification.importation_permission_groups');
      refresh();
      setOpenImportPermissionGroups(false);
    }
    setLoadingForImport(false);
  };
  return (
    <Dialog
      fullWidth
      open={open}
      aria-label="Import Permission Groups From Existing Contact"
      maxWidth={false}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
      classes={{
        paperWidthFalse: classes.paperWidthFalse // class name, e.g. `classes-nesting-root-x`
      }}
    >
      <DialogTitleWithCloseButton onClose={handleClose}>
        {'Import Permission Groups From Existing Contact'}
      </DialogTitleWithCloseButton>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.stepperContainer}>
          <Stepper activeStep={currentStep.index} className={classes.stepper}>
            {steps.map(step => (
              <Step key={step.index}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
        <div className={classes.stepContainer}>{getActiveStep}</div>
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}>
        <div className={classes.leftWrapper}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            className={classes.button}
          >
            Cancel
          </Button>

          {currentStep.backButton && (
            <Button
              onClick={decreaseStep}
              variant="outlined"
              color="primary"
              className={classes.backButton}
            >
              Back
            </Button>
          )}
        </div>

        <Button
          onClick={
            currentStep.nextButton ? increaseStep : handleImportAuthorizations
          }
          color="primary"
          variant="contained"
          className={classes.button}
          disabled={userToCopy === null}
        >
          {loadingForImport && (
            <CircularProgress className={classes.progressIndicator} />
          )}
          {currentStep.nextButton ? 'Next' : 'Import'}
        </Button>
      </DialogActions>
      <Confirm
        isOpen={openConfirm}
        title="Import Permission Groups From Existing Contact"
        content="You are canceling your import, are you sure you want to proceed?"
        onConfirm={handleClickSave}
        onClose={() => setOpenConfirm(false)}
      />
    </Dialog>
  );
};

export default ImportPermissionsGroupsDialog;
